import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, X } from "lucide-react";
import { useWhatsappSession } from "@/lib/hooks/useWhatsapp";
import { useConnectionsRealtime, useMtprotoSession } from "@/lib/hooks/useConnections";
import { dropDismissKey, isDroppedSession, type ChannelSessionRow } from "@/lib/connections";

// Alerta global de queda de sessão (WhatsApp ou Telegram userbot). Fica no
// layout do app: também é quem mantém a assinatura Realtime das conexões, então
// status e alerta chegam ao vivo em qualquer tela.

function readDismissed(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Storage bloqueado: o alerta só some até recarregar a página.
  }
}

interface DropEntry {
  channel: string;
  label: string;
  key: string;
}

function visibleDrops(
  candidates: ReadonlyArray<{ channel: string; label: string; row: ChannelSessionRow | null }>,
  hidden: readonly string[],
): DropEntry[] {
  const drops: DropEntry[] = [];
  for (const c of candidates) {
    if (!c.row || !isDroppedSession(c.row)) continue;
    const key = dropDismissKey(c.channel, c.row);
    if (!hidden.includes(key) && !readDismissed(key)) {
      drops.push({ channel: c.channel, label: c.label, key });
    }
  }
  return drops;
}

export function ConnectionDropAlert(): JSX.Element | null {
  useConnectionsRealtime();
  const { data: whatsapp } = useWhatsappSession();
  const { data: telegram } = useMtprotoSession();
  const [hidden, setHidden] = useState<readonly string[]>([]);

  const candidates: ReadonlyArray<{ channel: string; label: string; row: ChannelSessionRow | null }> = [
    { channel: "whatsapp", label: "WhatsApp", row: whatsapp ?? null },
    { channel: "telegram", label: "Telegram", row: telegram ?? null },
  ];
  const drops = visibleDrops(candidates, hidden);

  if (drops.length === 0) return null;

  const dismiss = (key: string): void => {
    writeDismissed(key);
    setHidden((prev) => [...prev, key]);
  };

  return (
    <div className="space-y-2">
      {drops.map((d) => (
        <div
          key={d.channel}
          role="alert"
          className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive"
        >
          <AlertTriangle size={16} className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            A conexão do {d.label} caiu e o monitoramento parou.{" "}
            <Link to="/app/settings/integracao" className="font-semibold underline underline-offset-2">
              Reconectar
            </Link>
          </span>
          <button
            type="button"
            aria-label="Dispensar alerta"
            onClick={() => dismiss(d.key)}
            className="rounded p-1 hover:bg-destructive/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
